/**
 * Diff `seedDoc` → `newAdfNode` via `@atlassian/content-reconciler` and
 * dispatch the resulting Steps as one `addToHistory: false` tx. Each Step
 * is rebased through `interveningMapping` (non-own txs since the seed)
 * before dispatch, making the writeback safe to interleave with remote
 * collab steps. The tx carries `OWN_WRITEBACK_META` so the orchestrator's
 * dispatch tap can echo-filter it synchronously.
 */
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Mapping } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { diffNodes } from '@atlassian/content-reconciler';
import type { DiffOptions } from '@atlassian/content-reconciler';

/** Meta tag set on every writeback tx so the orchestrator's tap can echo-filter it. */
export const OWN_WRITEBACK_META = 'mdMode$ownWriteback';

export const applyDiffSteps = (
	editorView: EditorView,
	seedDoc: PMNode,
	newAdfNode: PMNode,
	interveningMapping: Mapping,
	opts?: DiffOptions,
): Transaction | null => {
	if (editorView.isDestroyed) {
		return null;
	}

	const steps = diffNodes(seedDoc, newAdfNode, opts);
	if (steps.length === 0) {
		return null;
	}

	const tr = editorView.state.tr;
	for (const step of steps) {
		const rebased = step.map(interveningMapping);
		if (!rebased) {
			throw new Error('applyDiffSteps: step did not survive rebase');
		}
		const mapped = rebased.map(tr.mapping);
		if (!mapped) {
			throw new Error('applyDiffSteps: step could not be mapped');
		}
		tr.step(mapped);
	}
	tr.setMeta('addToHistory', false);
	tr.setMeta(OWN_WRITEBACK_META, true);
	editorView.dispatch(tr);
	return tr;
};
